import { useEffect } from "react";
import { useToast } from "@hiya/ui";
import { listResultsReceived, markTabViewed } from "../../../api/orders";
import { useKitList } from "../../../hooks/useKitList";
import { useAssignment } from "../../../hooks/useAssignment";
import { KitListLayout } from "../../../components/orders/KitListLayout";
import { ResultsStatusBadge } from "../../../components/orders/KitStatusBadge";
import type { ResultsStatus } from "../../../components/orders/KitStatusBadge";
import { DoctorActions } from "../../../components/orders/DoctorActions";
import { CloseIcon } from "../../../components/orders/CloseIcon";
import { AssignmentDialogs } from "../../../components/orders/AssignmentDialogs";
import { formatDate } from "../../../utils/formatDate";
import type { KitRecord } from "../../../types/orders";

const COLUMNS = ["User Name", "Day of Cycle", "Order ID", "Kit ID", "Received Date", "Assigned Doctor", "Status", "Actions"] as const;

function deriveResultsStatus(item: KitRecord): ResultsStatus {
  if (!item.assigned_doctor_id) return "Awaiting Assignment";
  if (item.stage === "draft_created" || item.admin_status === "Report Published") return "Draft Created";
  return "Awaiting Review";
}

export function ResultsReceivedPage() {
  const { toast, showToast } = useToast();
  const kit = useKitList(listResultsReceived);
  const assign = useAssignment({ updateItem: kit.updateItem, showToast });

  useEffect(() => {
    markTabViewed("results_received").catch(() => showToast("Something went wrong. Please refresh the page.", "warning"));
  }, [showToast]);

  return (
    <KitListLayout
      kit={kit}
      toast={toast}
      columns={COLUMNS}
      rowClassName={(item) => (item.is_overdue ? "bg-brand-brown-6" : "")}
      renderRow={(item) => (
        <>
          <td className="px-4 py-3 text-sm text-text-primary">{item.user_name || "-"}</td>
          <td className="px-4 py-3 text-sm text-text-primary">{item.cycle_dayphase || "-"}</td>
          <td className="px-4 py-3 text-sm text-text-primary">{item.order_id}</td>
          <td className="px-4 py-3 text-sm text-text-primary">{item.kit_code}</td>
          <td className="px-4 py-3 text-sm text-text-primary">{formatDate(item.updated_at)}</td>
          <td className="px-4 py-3">
            {item.assigned_doctor_id ? (
              <div className="flex flex-col items-center gap-0.5">
                <div className="flex items-center gap-1">
                  <span className="text-sm text-text-primary">{item.assigned_doctor_name}</span>
                  <button
                    onClick={() => assign.handleUnassign(item)}
                    className="text-text-tertiary hover:text-system-red shrink-0"
                    aria-label="Unassign doctor"
                  >
                    <CloseIcon />
                  </button>
                </div>
                {item.assigned_doctor_email && (
                  <span className="text-xs text-text-tertiary">{item.assigned_doctor_email}</span>
                )}
                {item.assigned_at && (
                  <span className="text-xs text-text-tertiary">{formatDate(item.assigned_at)}</span>
                )}
              </div>
            ) : (
              <span className="text-sm text-text-tertiary">Not assigned</span>
            )}
          </td>
          <td className="px-4 py-3">
            <ResultsStatusBadge status={deriveResultsStatus(item)} />
          </td>
          <td className="px-4 py-3">
            <DoctorActions kit={item} onAssign={assign.handleAssign} onReassign={assign.handleReassign} />
          </td>
        </>
      )}
    >
      <AssignmentDialogs assign={assign} />
    </KitListLayout>
  );
}
